import gsap from "gsap";
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
gsap.registerPlugin(ScrollSmoother);
gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(SplitText);
export default class Animations {
    constructor() {
        this.smoother = null;
        // Prevent the browser from jumping to the hash anchor before GSAP
        // initializes — otherwise ScrollTrigger calculates positions while
        // already scrolled down, leaving animated elements stuck invisible.
        if (window.location.hash) {
            history.scrollRestoration = 'manual';
            window.scrollTo(0, 0);
        }
        // init animations after DOM is ready
        document.addEventListener('DOMContentLoaded', () => {
            // init smooth scroll
            this.smoother = ScrollSmoother.create({
                wrapper: '#smooth-wrapper',
                content: '#smooth-content',
                smooth: 1,
                effects: true,
            });
            this.initPagePreloadAnimation();
            this.initBlockAnimations();
            // functions for custom block animations can be added here
            this.initBlockLinkAnimation();
            this.initServicesBlockAnimation();
            this.initProductsBlockAnimation();
            this.initAppBlockAnimation();
            this.iniContactsBlockAnimation();
            this.initFadeUpScrollAnimation();
            this.initNewsBlockAnimation();
            this.initTeamBlockAnimation();
            this.initLatestNewsBlockAnimation();
            this.initAboutTeamBlockAnimation();
            // After full page load scroll to hash so all ScrollTriggers have
            // had a chance to register their positions from scroll offset 0.
            window.addEventListener('load', () => {
                this.scrollToHash();
            });
            this.initAnchorLinkScroll();
        });
    }
    scrollToHash() {
        const hash = window.location.hash;
        if (!hash)
            return;
        const target = document.querySelector(hash);
        if (!target || !this.smoother)
            return;
        setTimeout(() => {
            ScrollTrigger.refresh();
            this.smoother.scrollTo(target, true);
        }, 100);
    }
    initAnchorLinkScroll() {
        document.addEventListener('click', (e) => {
            const link = e.target.closest('a');
            if (!link)
                return;
            const hash = link.hash;
            if (!hash || hash.length < 2)
                return;
            // Only intercept same-page hash links (covers both "#id" and "/path#id" on current page).
            if (link.origin !== window.location.origin)
                return;
            if (link.pathname !== window.location.pathname)
                return;
            let target = null;
            try {
                target = document.querySelector(hash);
            }
            catch (_a) {
                return;
            }
            if (!target || !this.smoother)
                return;
            e.preventDefault();
            this.smoother.scrollTo(target, true, 'top top');
            history.replaceState(null, '', hash);
        });
    }
    /**
     * Preload animation with overlay and svg, which moves up and fades out on page load,
     * revealing the content with a nice animation on the hero headline characters.
     */
    initPagePreloadAnimation() {
        const preloadOverlay = document.querySelector('.preload-overlay');
        if (!preloadOverlay)
            return;
        const preloadContent = preloadOverlay.querySelector('.preload-center .preload-svg');
        if (!preloadOverlay)
            return;
        const self = this;
        const timeline = gsap.timeline();
        timeline.to(preloadOverlay, {
            opacity: 1,
            duration: 1,
            ease: 'power3.out',
        });
        window.addEventListener('load', () => {
            const chars = self.getHeroChars();
            gsap.set(chars, { opacity: 0, y: 50, scale: 0.8, rotate: 20, filter: "blur(5px)" });
            timeline.to(preloadContent, {
                y: '-101vh',
                duration: 1,
                ease: 'power3.out',
            });
            timeline.to(preloadOverlay, {
                opacity: 0,
                duration: 0.1,
                ease: 'power3.out',
                onComplete: () => {
                    preloadOverlay.remove();
                    if (chars) {
                        gsap.to(chars, { opacity: 1, y: 0, scale: 1, rotate: 0, filter: "blur(0px)", duration: 1.2, ease: 'elastic.out(1, 0.8)', stagger: 0.03 });
                    }
                }
            });
        });
    }
    getHeroChars() {
        const heroSection = document.querySelector('.block.hero-split.with-video');
        if (!heroSection)
            return;
        const splitText = heroSection.querySelector('.animate-h');
        if (!splitText)
            return;
        const split = new SplitText(splitText, { type: 'words,chars' });
        return split.chars;
    }
    initBlockAnimations() {
        const self = this;
        const blocks = document.querySelectorAll('.block');
        if (!blocks)
            return;
        // Only the preloader animates the with-video hero h1; if there's no
        // preloader on the page, fall through and let this routine animate it.
        const hasPreloader = !!document.querySelector('.preload-overlay');
        blocks.forEach((block) => {
            if (block.classList.contains('with-video') && hasPreloader)
                return;
            const splitHeaders = block.querySelectorAll('.animate-split-hb');
            splitHeaders.forEach((header) => {
                const split = new SplitText(header, { type: 'words,chars' });
                gsap.set(header, { opacity: 1 });
                gsap.from(split.chars, {
                    scrollTrigger: {
                        trigger: block,
                        start: 'top 75%',
                        once: true
                    },
                    opacity: 0,
                    y: 0,
                    duration: 1,
                    ease: 'power3.out',
                    stagger: 0.05,
                });
            });
            const fadeInElements = document.querySelectorAll('.block .fade-in-up');
            fadeInElements.forEach((element) => {
                gsap.set(element, { opacity: 0, y: 15 });
                gsap.to(element, {
                    scrollTrigger: {
                        trigger: block,
                        start: 'top 75%',
                        once: true,
                    },
                    opacity: 1,
                    y: 0,
                    delay: 0.4,
                    duration: 0.8,
                    ease: 'power3.out',
                    stagger: 0.1,
                });
            });
            const slideInGroups = document.querySelectorAll('.block:not(.latest-news) .animate-slide-in-group');
            if (slideInGroups.length > 0) {
                // change group element trigger based on viewport width, to trigger earlier on smaller screens
                const isMobile = window.innerWidth < 768;
                slideInGroups.forEach((group) => {
                    const children = group.querySelectorAll('.animate-slide-in-group-element');
                    if (isMobile) {
                        children.forEach((child) => {
                            // get first element
                            const firstElementChild = child.firstElementChild;
                            gsap.set(firstElementChild, self.setSlideUpAnimation());
                            gsap.to(firstElementChild, self.getSlideUpAnimation(child));
                        });
                    }
                    else {
                        gsap.set(children, self.setSlideUpAnimation());
                        gsap.to(children, self.getSlideUpAnimation(group));
                    }
                });
            }
        });
    }
    initBlockLinkAnimation() {
        const block = document.querySelector('.block.link-cards');
        if (!block)
            return;
        const cards = block.querySelectorAll('.card-link');
        if (!cards)
            return;
        cards.forEach((card) => {
            const video = card.querySelector('video');
            const contentWrapper = card.querySelector('.card-content-wrapper');
            const contents = contentWrapper.querySelectorAll('div');
            gsap.set(video, { scale: 0.5, opacity: 0 });
            gsap.set(contents, { y: 20, opacity: 0 });
            gsap.to(video, {
                scrollTrigger: {
                    trigger: card,
                    start: 'top 80%',
                    once: true,
                },
                scale: 1,
                opacity: 1,
                duration: 0.8,
                ease: 'power3.out',
            });
            gsap.to(contents, {
                scrollTrigger: {
                    trigger: contentWrapper,
                    start: 'top 90%',
                    once: true,
                },
                y: 0,
                opacity: 1,
                delay: 0.3,
                duration: 0.8,
                stagger: 0.1,
                ease: 'power3.out',
            });
        });
    }
    initServicesBlockAnimation() {
        const block = document.querySelector('.block.services');
        if (!block)
            return;
        const cards = block.querySelectorAll('.card-service');
        if (!cards)
            return;
        if (cards.length !== 2)
            return;
        const [firstCard, secondCard] = cards;
        // block animate opacity based on scroll progress
        gsap.set(firstCard, { opacity: 0, rotation: -30, transformOrigin: "center bottom" });
        gsap.set(secondCard, { opacity: 0, rotation: 30, transformOrigin: "center bottom" });
        if (window.innerWidth < 768) {
            gsap.to(firstCard, {
                opacity: 1,
                rotation: 0, // Starts tilted -45 degrees
                transformOrigin: "center bottom", // Rotates around its center
                scrollTrigger: {
                    trigger: firstCard,
                    start: "top 95%", // Starts when the top of the object is near the bottom
                    end: "top 50%", // Ends before it reaches the center
                    scrub: 1,
                }
            });
            gsap.to(secondCard, {
                opacity: 1,
                rotation: 0, // Starts tilted -45 degrees
                transformOrigin: "center bottom", // Rotates around its center
                scrollTrigger: {
                    trigger: secondCard,
                    start: "top 90%", // Starts when the top of the object is near the bottom
                    end: "top 50%", // Ends before it reaches the center
                    scrub: 1,
                }
            });
        }
        else {
            gsap.to([firstCard, secondCard], {
                opacity: 1,
                rotation: 0, // Starts tilted -45 degrees
                transformOrigin: "center bottom", // Rotates around its center
                scrollTrigger: {
                    trigger: block,
                    start: "top 90%", // Starts when the top of the object is near the bottom
                    end: "top 10%", // Ends before it reaches the center
                    scrub: 1, // "1" adds a slight smooth catch-up delay
                }
            });
        }
    }
    initProductsBlockAnimation() {
        const block = document.querySelector('.block.products');
        if (!block)
            return;
        const items = block.querySelectorAll('.card-product');
        if (!items)
            return;
        const timeline = gsap.timeline({
            scrollTrigger: {
                trigger: block,
                start: 'top 80%',
                end: 'top 20%',
                scrub: 1,
            },
        });
        let i = 20;
        let r = -2;
        items.forEach((item) => {
            gsap.set(item, { opacity: 1, y: `${i}%`, rotateX: r, scale: 0.9, transformOrigin: "center center" });
            i === 20 ? i = 40 : i = 20;
            r -= 1;
            timeline.to(item, {
                opacity: 1,
                y: 0,
                rotateX: 0,
                scale: 1,
                duration: 3,
            }, 0);
            const image = item.querySelector('img');
            if (image) {
                gsap.set(image, { scale: 0.75, transformOrigin: "center bottom" });
                timeline.to(image, {
                    scale: 1,
                    duration: 3,
                    ease: 'power3.out',
                }, 1.5);
            }
        });
    }
    initAppBlockAnimation() {
        const block = document.querySelector('.block.app');
        if (!block)
            return;
        const image = block.querySelector('.block-image img');
        if (!image)
            return;
        const timeline = gsap.timeline({
            scrollTrigger: {
                trigger: block,
                start: 'top 100%',
                end: 'top 20%',
                scrub: 1,
            },
        });
        gsap.set(image, { scale: 0.9, rotate: 15, transformOrigin: "center center" });
        timeline.to(image, {
            scale: 1,
            rotate: 0,
            duration: 2,
            ease: 'power3.out',
        }, 0);
        const content = block.querySelector('.animate-content');
        gsap.set(content, { opacity: 0, y: 50 });
        timeline.to(content, {
            opacity: 1,
            y: 0,
            duration: 1,
        }, 1);
    }
    iniContactsBlockAnimation() {
        const block = document.querySelector('.block.contacts');
        if (!block)
            return;
        const video = block.querySelector('.block-video');
        const blockTitle = block.querySelector('.block-title');
        const contactInfo = block.querySelector('.contact-info ');
        if (!video)
            return;
        const timeline = gsap.timeline({
            scrollTrigger: {
                trigger: block,
                start: 'top 80%',
                end: 'top 20%',
                scrub: 1,
            },
        });
        gsap.set(video, { scale: 0.5, opacity: 0, transformOrigin: "center bottom" });
        gsap.set([blockTitle, contactInfo], { opacity: 0, y: 20, scale: 0.75 });
        timeline.to(video, {
            scale: 1,
            opacity: 1,
            duration: 5,
            ease: 'power3.out',
        }, 0);
        timeline.to([blockTitle, contactInfo], {
            opacity: 1,
            y: 0,
            duration: 2,
            scale: 1,
            stagger: 0.6,
        }, 2);
    }
    initFadeUpScrollAnimation() {
        const fadeBlocks = document.querySelectorAll('.block-fade-up-scroll');
        fadeBlocks.forEach((block) => {
            // News and team blocks have their own per-card animations
            if (block.closest('.block.news'))
                return;
            if (block.closest('.block.team'))
                return;
            const elements = block.querySelectorAll('.fade-up-scroll-element');
            gsap.set(elements, { opacity: 0, y: 70, rotate: 5 });
            gsap.to(elements, {
                scrollTrigger: {
                    trigger: block,
                    start: 'top 80%',
                    once: true,
                },
                opacity: 1,
                y: 0,
                rotate: 0,
                duration: 0.6,
                stagger: 0.1,
            });
        });
    }
    initNewsBlockAnimation() {
        const block = document.querySelector('.block.news');
        if (!block)
            return;
        const topNewsCards = block.querySelectorAll('.top-news .fade-up-scroll-element');
        topNewsCards.forEach((card) => {
            const x = gsap.utils.random(-80, 80);
            const rotation = gsap.utils.random(-15, 15);
            gsap.set(card, { opacity: 0, y: 100, x, rotation });
        });
        gsap.to(topNewsCards, {
            opacity: 1,
            y: 0,
            x: 0,
            rotation: 0,
            duration: 1,
            ease: 'power3.out',
            stagger: 0.15,
            delay: 0.3,
        });
        const cards = block.querySelectorAll('.fade-up-scroll-element');
        cards.forEach((card) => {
            if (card.closest('.top-news'))
                return;
            Animations.animateNewsCard(card);
        });
    }
    static animateNewsCard(card) {
        const x = gsap.utils.random(-80, 80);
        const rotation = gsap.utils.random(-15, 15);
        gsap.set(card, { opacity: 0, y: 100, x, rotation });
        gsap.to(card, {
            opacity: 1,
            y: 0,
            x: 0,
            rotation: 0,
            duration: 1,
            ease: 'power2.out',
            scrollTrigger: {
                trigger: card,
                start: 'top 90%',
                end: 'top 60%',
                scrub: 1,
            },
        });
    }
    initAboutTeamBlockAnimation() {
        const block = document.querySelector('.block.about-team');
        if (!block)
            return;
        const rows = block.querySelectorAll('.row');
        if (!rows.length)
            return;
        rows.forEach((row, i) => {
            const image = row.querySelector('.card-image');
            const content = row.querySelector('.col-sm');
            const isReverse = row.classList.contains('reverse');
            const rotateFrom = isReverse ? 12 : -12;
            if (image) {
                gsap.set(image, { opacity: 0, rotation: rotateFrom, scale: 0.85, transformOrigin: 'center center' });
                gsap.to(image, {
                    opacity: 1,
                    rotation: 0,
                    scale: 1,
                    duration: 1,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: row,
                        start: 'top 85%',
                        toggleActions: 'play reverse play reverse',
                    },
                });
            }
            if (content) {
                gsap.set(content, { opacity: 0, y: 30 });
                gsap.to(content, {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    delay: 0.2,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: row,
                        start: 'top 85%',
                        toggleActions: 'play reverse play reverse',
                    },
                });
            }
        });
    }
    initTeamBlockAnimation() {
        const block = document.querySelector('.block.team');
        if (!block)
            return;
        const cards = block.querySelectorAll('.fade-up-scroll-element');
        if (!cards.length)
            return;
        cards.forEach((card) => Animations.animateNewsCard(card));
    }
    initLatestNewsBlockAnimation() {
        const block = document.querySelector('.block.latest-news');
        if (!block)
            return;
        const cards = block.querySelectorAll('.animate-slide-in-group-element');
        if (!cards.length)
            return;
        const yOffsets = [80, 120, 160];
        const timeline = gsap.timeline({
            scrollTrigger: {
                trigger: block,
                start: 'top 80%',
                end: 'top 30%',
                scrub: 1,
            },
        });
        cards.forEach((card, i) => {
            var _a;
            const y = (_a = yOffsets[i]) !== null && _a !== void 0 ? _a : 100;
            gsap.set(card, { opacity: 0, y });
            timeline.to(card, {
                opacity: 1,
                y: 0,
                duration: 1,
                ease: 'power2.out',
            }, 0);
        });
    }
    setSlideUpAnimation() {
        return { y: '30vh', rotate: 10 };
    }
    getSlideUpAnimation(triggerElement) {
        return {
            scrollTrigger: {
                trigger: triggerElement,
                start: 'top 80%',
                once: true,
            },
            opacity: 1,
            y: 0,
            rotate: 0,
            scale: 1,
            duration: 0.6,
            stagger: 0.1,
        };
    }
}
